import { lazy } from "react";
import {
  Building2,
  FileSpreadsheet,
  Layers,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";

import { CUSTOMER_TAB_ROUTES } from "@/pages/customers/customer-tabs";
import { DEAL_TAB_ROUTES } from "@/pages/deals/deal-tabs";
import { SUPPLIER_TAB_ROUTES } from "@/pages/suppliers/supplier-tabs";
import type { AppRoute } from "@/lib/routes";
import {
  ACCOUNTS_PATH,
  CLIENTS_PATH,
  DEALS_PATH,
  SUPPLIERS_PATH,
  UTILITIES_PATH,
  clientPath,
  dealPath,
  supplierPath,
} from "@/lib/record-paths";

/**
 * Shared nav modules — the record layer: the entities every module sits on top
 * of, owned by none of them.
 *
 * This is the Salesforce/Dynamics shape: in Salesforce a *tab* is a
 * standalone declaration and a **module** is a selection of tabs; in Dynamics a
 * sitemap subarea points at a shared Dataverse table that many module areas
 * surface. The record is declared once here. Clients and Accounts belong to
 * Sales, Market Intelligence, and Energy Insights alike — none of them
 * *owns* the client.
 *
 * Replaces the previous "Global module": a separate destination users had to
 * travel to (with its own sidebar) in order to reach a client. That made
 * every drill-in an involuntary module switch. Records are module-neutral now —
 * one canonical url each (`lib/record-paths.ts`), rendered inside whichever
 * module's chrome you're already in (`lib/module-context.ts`).
 *
 * **Where they render: the horizontal `RecordBar` under the topbar**
 * (`components/layout/RecordBar.tsx`), in the modules that set `recordBar`
 * (Energy Advisor and Operations) — not as a section in any module's sidebar. They were briefly a "Records" section
 * inside each sidebar, which put them visually beneath the module pill as a
 * peer of "Workspace" and "Operations" — implying the current module owned
 * them, the exact opposite of the point. No label fixes that, since every
 * label inherits the same position and styling; the fix is different real
 * estate. Salesforce Lightning does the same thing with its horizontal
 * object-tab bar.
 *
 * **Adding a record module:** declare it here. It appears in the record bar
 * automatically (subject to its own auth gate) and its routes register via
 * `recordRoutes()`. Nothing to add to any module definition.
 */

// Lazy page chunks. These pages were previously lazily imported by
// `global-routes.tsx`; they move here so the module owns its own element and
// no module registry re-declares them.
const AccountsPage = lazy(() =>
  import("@/pages/accounts/AccountsPage").then((m) => ({
    default: m.AccountsPage,
  })),
);
const CustomersPage = lazy(() =>
  import("@/pages/customers/CustomersPage").then((m) => ({
    default: m.CustomersPage,
  })),
);
const CustomerDetailPage = lazy(() =>
  import("@/pages/customers/CustomerDetailPage").then((m) => ({
    default: m.CustomerDetailPage,
  })),
);
const SuppliersPage = lazy(() =>
  import("@/pages/suppliers/SuppliersPage").then((m) => ({
    default: m.SuppliersPage,
  })),
);
const SupplierDetailPage = lazy(() =>
  import("@/pages/suppliers/SupplierDetailPage").then((m) => ({
    default: m.SupplierDetailPage,
  })),
);
const UtilitiesPage = lazy(() =>
  import("@/pages/utilities/UtilitiesPage").then((m) => ({
    default: m.UtilitiesPage,
  })),
);
const DealsPage = lazy(() =>
  import("@/pages/deals/DealsPage").then((m) => ({ default: m.DealsPage })),
);
const DealDetailPage = lazy(() =>
  import("@/pages/deals/DealDetailPage").then((m) => ({
    default: m.DealDetailPage,
  })),
);

/** Stable id for a shared record, referenced from `MODULE_REGISTRY`. */
export type NavRecordKey =
  | "clients"
  | "accounts"
  | "deals"
  | "suppliers"
  | "utilities";

export interface NavRecord {
  key: NavRecordKey;
  /** Sidebar label + icon. Identical in every module that surfaces it, so the
   *  record reads as the same thing wherever the user meets it. */
  label: string;
  icon: LucideIcon;
  /**
   * The sidebar entry (the list page). Gets `section` assigned by the module
   * composing it, so one module can file Clients under "Records" while another
   * files it elsewhere without the module caring.
   */
  navRoute: Omit<AppRoute, "section">;
  /**
   * Routes the module registers with the router but does NOT put in a
   * sidebar — detail pages, reached by drilling in. Registered once
   * globally, not per module: a record has one canonical url, so registering it
   * per-module would create duplicate route definitions for the same path.
   */
  detailRoutes?: AppRoute[];
}

export const NAV_RECORDS: Record<NavRecordKey, NavRecord> = {
  clients: {
    key: "clients",
    label: "Clients",
    icon: Users,
    navRoute: {
      path: CLIENTS_PATH,
      label: "Clients",
      icon: Users,
      status: "live",
      element: <CustomersPage />,
      requiredPermission: "customers.list",
    },
    detailRoutes: [
      {
        // Hidden from every sidebar (no `section`) — reached by drilling in.
        // `children` declares the tab structure; the router redirects the
        // bare path to the caller's first authorized tab.
        //
        // Tab arrays stay in their own co-located module
        // (`customers/customer-tabs.tsx`) rather than inline here: this file
        // imports `CustomerDetailPage`, and `CustomerDetailPage` imports the
        // tab routes — inlining them would close that cycle.
        path: `${CLIENTS_PATH}/:id`,
        label: "Client",
        status: "live",
        element: <CustomerDetailPage />,
        breadcrumbs: [{ label: "Clients", href: CLIENTS_PATH }, { label: "…" }],
        children: [...CUSTOMER_TAB_ROUTES],
        requiredPermission: "customers.view",
      },
    ],
  },

  accounts: {
    key: "accounts",
    label: "Accounts",
    icon: FileSpreadsheet,
    navRoute: {
      path: ACCOUNTS_PATH,
      label: "Accounts",
      icon: FileSpreadsheet,
      status: "live",
      element: <AccountsPage />,
      requiredPermission: "accounts.list",
    },
    // No detail route today — accounts are edited in a sheet from the list
    // and from the client detail page's Accounts tab.
  },

  deals: {
    key: "deals",
    label: "Deals",
    icon: Layers,
    navRoute: {
      path: DEALS_PATH,
      label: "Deals",
      icon: Layers,
      status: "live",
      element: <DealsPage />,
      requiredPermission: "deals.list",
    },
    detailRoutes: [
      {
        // Hidden from every sidebar (no `section`) — reached by drilling in
        // from the deals list or a client's Deals tab. Tab routes stay in
        // their own co-located module for the same cycle-breaking reason as
        // Clients above.
        path: `${DEALS_PATH}/:id`,
        label: "Deal",
        status: "live",
        element: <DealDetailPage />,
        breadcrumbs: [{ label: "Deals", href: DEALS_PATH }, { label: "…" }],
        children: [...DEAL_TAB_ROUTES],
        requiredPermission: "deals.view",
      },
    ],
  },

  suppliers: {
    key: "suppliers",
    label: "Suppliers",
    icon: Building2,
    navRoute: {
      path: SUPPLIERS_PATH,
      label: "Suppliers",
      icon: Building2,
      status: "live",
      element: <SuppliersPage />,
      requiredPermission: "suppliers.view",
    },
    detailRoutes: [
      {
        path: `${SUPPLIERS_PATH}/:id`,
        label: "Supplier",
        status: "live",
        element: <SupplierDetailPage />,
        breadcrumbs: [
          { label: "Suppliers", href: SUPPLIERS_PATH },
          { label: "…" },
        ],
        children: [...SUPPLIER_TAB_ROUTES],
        requiredPermission: "suppliers.view",
      },
    ],
  },

  utilities: {
    key: "utilities",
    label: "Utilities",
    icon: Zap,
    navRoute: {
      path: UTILITIES_PATH,
      label: "Utilities",
      icon: Zap,
      status: "live",
      element: <UtilitiesPage />,
      requiredPermission: "utilities.view",
    },
    // No detail route today.
  },
};

/**
 * The record layer that appears in the cross-module `RecordBar`, **A–Z by
 * label** — Accounts, Clients, Deals.
 *
 * **Deliberately not every record in `NAV_RECORDS`.** Being declared as a
 * record means "declared once, no module owns the route"; it does NOT
 * automatically mean "belongs in the product-wide record bar." Suppliers and
 * Utilities are the cases in point: each is a record (one canonical url, one
 * declaration) but only Sales works with either today, so both are pinned
 * into that module's Operations section instead — see `SALES_MODULE`'s
 * `records`. Giving them product-wide billing overstated their reach.
 *
 * Promote a record here when a second module genuinely needs it; until then a
 * single-module record belongs in that module's own nav.
 *
 * Hand-listed rather than derived from `NAV_RECORDS` precisely so adding a
 * record is not silently a decision to put it in the record bar. Sorting is
 * still derived, on the record's `label` rather than its key — `clients`'s
 * label is "Clients", so the two orders would diverge for any record whose
 * key and label differ.
 */
export const ALL_RECORDS: NavRecordKey[] = (
  ["accounts", "clients", "deals"] as NavRecordKey[]
).sort((a, b) => NAV_RECORDS[a].label.localeCompare(NAV_RECORDS[b].label));

/**
 * Every shared module's routes — list page plus detail pages — for one-time
 * router registration.
 *
 * Registered here, independently of any module's nav, because a record's URL
 * must exist whether or not some module currently chooses to put it in a
 * sidebar. Composing a module into a module's nav is a *navigation* decision;
 * it must not be what makes the route resolve. Reports dropping its Records
 * section is exactly that case — `/clients` still has to work, since links
 * to it come from all over the module (dashboards, search, Maxwell, recents).
 *
 * The nav entries a module composes carry the same paths, so `router.tsx`
 * dedupes by path; these registrations win because they come first.
 */
export function recordRoutes(): AppRoute[] {
  return Object.values(NAV_RECORDS).flatMap((m) => [
    // No `section`: the canonical registration is router-only. A module that
    // surfaces this module contributes its own sidebar-visible copy via
    // `recordNavRoute`.
    { ...m.navRoute } as AppRoute,
    ...(m.detailRoutes ?? []),
  ]);
}

/**
 * Materialize a module's sidebar entry into a module's own section.
 *
 * The module supplies path/element/gates/icon; the composing module supplies
 * which section header it sits under. Same `AppRoute` shape every other nav
 * entry uses, so `getNavSections`, `isRouteAuthorized`, and the row
 * primitives in `nav-primitives.tsx` need no knowledge of modules at all.
 */
export function recordNavRoute(
  key: NavRecordKey,
  section: string,
): AppRoute {
  return { ...NAV_RECORDS[key].navRoute, section } as AppRoute;
}


/** Canonical detail-path builders, re-exported so callers have one import. */
export { clientPath, dealPath, supplierPath };
